/**
 * Control Inheritance — React Hook
 *
 * Resolves all inherited controls in a schema before the form renders.
 * All resolutions run in parallel.
 *
 * Resolution is skipped in 'edit' and 'design' modes so the designer sees
 * the raw control definition (with referredFormID / referredControlID intact).
 * In 'preview', 'view', and 'admin' modes the fully resolved schema is used.
 *
 * override — 1-to-1: one inherited control → one merged control
 * import   — 1-to-N: one placeholder control → N controls from the base form
 *
 * Controls that fail resolution are replaced with an `_inheritance-error`
 * placeholder so the rest of the form still renders normally.
 *
 * Returns the raw schema synchronously when resolution is skipped or when
 * no controls are inherited — zero overhead in those cases.
 */

import { useEffect, useState } from 'react';
import type { FormSchema, FormControl, OperatingMode } from '@atlas-forms/types-js';
import { isInheritedControl, INHERITANCE_ERROR_TYPE, type InheritanceErrorControl } from './types';
import { fetchInheritedSchema } from './cache';
import { resolveOverride, resolveImport } from './resolver';

export interface ResolvedSchemaResult {
  /** Fully resolved schema. Equals the raw input when resolution is skipped. */
  schema: FormSchema;
  /** True only while at least one inherited control is being fetched. */
  loading: boolean;
  /** controlId → error message for any control that failed to resolve. */
  errors: Record<string, string>;
}

/** Modes in which inheritance is resolved. */
const RESOLVE_MODES = new Set<OperatingMode>(['preview', 'view', 'admin']);

type Resolution =
  | { ctrls: FormControl[]; error: null }
  | { ctrls: FormControl[]; error: { id: string; message: string } };

/**
 * State tracks WHICH rawSchema the current result was resolved for.
 * This lets us compute loading=true synchronously when mode switches to a
 * resolving mode and the new schema hasn't been resolved yet, avoiding any
 * render gap where loading=false but the schema still contains raw markers.
 */
interface ResolverState {
  resolvedFor: FormSchema | null;
  schema: FormSchema;
  errors: Record<string, string>;
}

export function useResolvedSchema(
  rawSchema: FormSchema,
  mode: OperatingMode = 'edit',
): ResolvedSchemaResult {
  const shouldResolve = RESOLVE_MODES.has(mode);
  const rawHasInherited = shouldResolve && (rawSchema.controls ?? []).some(isInheritedControl);

  const [state, setState] = useState<ResolverState>({
    resolvedFor: null,
    schema: rawSchema,
    errors: {},
  });

  // loading is derived synchronously — true when resolution is needed but the
  // cached state hasn't been resolved for this exact rawSchema yet.
  const loading = rawHasInherited && state.resolvedFor !== rawSchema;

  useEffect(() => {
    if (!shouldResolve) {
      setState({ resolvedFor: null, schema: rawSchema, errors: {} });
      return;
    }

    const controls = rawSchema.controls ?? [];
    if (!controls.some(isInheritedControl)) {
      setState({ resolvedFor: rawSchema, schema: rawSchema, errors: {} });
      return;
    }

    let cancelled = false;

    Promise.all(
      controls.map(async (ctrl): Promise<Resolution> => {
        if (!isInheritedControl(ctrl)) return { ctrls: [ctrl], error: null };

        const inheritType = ctrl.controlInheritType ?? 'override';

        try {
          if (inheritType === 'import') {
            const imported = await resolveImport(ctrl, fetchInheritedSchema);
            return { ctrls: imported, error: null };
          } else {
            const resolved = await resolveOverride(ctrl, fetchInheritedSchema);
            return { ctrls: [resolved], error: null };
          }
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to resolve inherited control';
          const placeholder: InheritanceErrorControl = {
            id: ctrl.id,
            type: INHERITANCE_ERROR_TYPE,
            label: ctrl.label ?? ctrl.id,
            _errorMessage: message,
          };
          return {
            ctrls: [placeholder as unknown as FormControl],
            error: { id: ctrl.id, message },
          };
        }
      }),
    )
      .then((resolutions) => {
        if (cancelled) return;

        const resolvedControls = resolutions.flatMap((r) => r.ctrls);

        // Warn on duplicate IDs — can happen after import expansion.
        const seenIds = new Set<string>();
        for (const ctrl of resolvedControls) {
          if (seenIds.has(ctrl.id)) {
            console.warn(
              `[control-inheritance] Duplicate control ID "${ctrl.id}" after resolving imports. ` +
              `This may cause unexpected behaviour — ensure imported control IDs are unique within this form.`,
            );
          }
          seenIds.add(ctrl.id);
        }

        const errors: Record<string, string> = {};
        resolutions.forEach((r) => {
          if (r.error) errors[r.error.id] = r.error.message;
        });

        setState({
          resolvedFor: rawSchema,
          schema: { ...rawSchema, controls: resolvedControls },
          errors,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[control-inheritance] Unexpected error during schema resolution:', err);
        setState({ resolvedFor: rawSchema, schema: rawSchema, errors: {} });
      });

    return () => {
      cancelled = true;
    };
  }, [rawSchema, shouldResolve]);

  // Not resolving: always return raw schema directly — never use stale state.
  if (!shouldResolve) {
    return { schema: rawSchema, loading: false, errors: {} };
  }

  // Resolving but no inherited controls in this schema: return raw directly.
  // Without this, state.schema could be a stale resolved schema from a previous
  // rawSchema until the effect runs and resets state.
  if (!rawHasInherited) {
    return { schema: rawSchema, loading: false, errors: {} };
  }

  return { schema: state.schema, loading, errors: state.errors };
}
